import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useState, useMemo } from 'react';
import { Button, Form, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Spinner from '../../shared/spinner';
import { ButtonAlterar } from '../../shared/buttons';
import LayoutRelatorioService from '../../services/layoutRelatorioService';
import ConfirmModal from '../../shared/customAlertConfirm';
import { useVerificaPermissao } from '../../shared/useVerificaPermissao';
import { useSlug } from '../hooks/UseSlug';
import { faFilter, faRefresh, faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ProtectedAction } from '../../AccessControl';
import ResponsivePagination from 'react-responsive-pagination'; // Importado

function ListagemLayoutRelatorioComponent() {
    useVerificaPermissao();
    const navigate = useNavigate();
    const slug = useSlug();

    const [loading, setLoading] = useState(true);
    const [layouts, setLayouts] = useState<any[]>([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [idParaExcluir, setIdParaExcluir] = useState<number | null>(null);

    // Estados da Paginação
    const [paginaAtual, setPaginaAtual] = useState(1);
    const [totalPaginas, setTotalPaginas] = useState(1);

    const [filtrosVisiveis, setFiltrosVisiveis] = useState(false);
    const [nomeFiltro, setNomeFiltro] = useState('');

    const layoutService = useMemo(() => new LayoutRelatorioService(), []);

    const listarLayouts = async (page = 1) => {
        try {
            setLoading(true);
            // Passamos a página (page - 1) pois o Spring inicia em 0
            const response = await layoutService.listarTodosLayouts(page - 1, 10);

            // Como o retorno agora é um Page<LayoutRelatorio>, acessamos .content
            const jsonPage = response.data;
            let dados = jsonPage.content || [];

            // Filtro em memória (Opcional, se quiser filtrar via API precisa passar no service)
            if (nomeFiltro) {
                dados = dados.filter((l: any) =>
                    l.nome.toLowerCase().includes(nomeFiltro.toLowerCase()) ||
                    l.tituloArquivo.toLowerCase().includes(nomeFiltro.toLowerCase())
                );
            }

            setLayouts(dados);
            setTotalPaginas(jsonPage.totalPages); // Atualiza total de páginas do banco
            setLoading(false);
        } catch (err) {
            setLoading(false);
            console.error("Erro ao listar layouts", err);
        }
    };

    const prepararExclusao = (id: number) => {
        setIdParaExcluir(id);
        setConfirmOpen(true);
    };

    const handleConfirmExclusao = async () => {
        if (idParaExcluir) {
            try {
                setLoading(true);
                await layoutService.excluirLayout(idParaExcluir);
                listarLayouts(paginaAtual);
            } catch (err) {
                console.error(err);
            } finally {
                setConfirmOpen(false);
                setIdParaExcluir(null);
            }
        }
    };

    // Recarrega sempre que a página mudar
    useEffect(() => {
        listarLayouts(paginaAtual);
    }, [paginaAtual]);

    return (
        <>
            <div className="position-relative m-md-3">
                <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between' }}>
                    <h5>Configurações &gt; Relatórios Personalizados</h5>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <Button id="App-button-cor-principal-escura" onClick={() => setFiltrosVisiveis(!filtrosVisiveis)}>
                            <FontAwesomeIcon icon={faFilter} style={{ marginRight: '10px' }} />
                            Filtros
                        </Button>

                        <ProtectedAction>
                            <Button
                                onClick={() => navigate(`${slug ? `/${slug}` : ''}/relatorios-personalizados`, { state: { layout: null } })}
                                id="App-button-cor-principal-escura"
                            >
                                <FontAwesomeIcon icon={faPlus} style={{ marginRight: '10px' }} />
                                Novo Layout
                            </Button>
                        </ProtectedAction>
                    </div>
                </div>

                {filtrosVisiveis && (
                    <div className="card p-3 mb-4 bg-light border-0 shadow-sm">
                        <Row className="align-items-end">
                            <Form.Group as={Col} md='6'>
                                <Form.Label className="fw-bold">Buscar por Nome ou Arquivo</Form.Label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={nomeFiltro}
                                    onChange={(e) => setNomeFiltro(e.target.value)}
                                    placeholder="Digite parte do nome..."
                                />
                            </Form.Group>
                            <Col md='2'>
                                <Button id="App-button-cor-principal-escura" className="w-100" onClick={() => listarLayouts(1)}>
                                    <FontAwesomeIcon icon={faRefresh} className="me-2" /> Filtrar
                                </Button>
                            </Col>
                        </Row>
                    </div>
                )}

                {loading ? (
                    <Spinner loading={loading} />
                ) : (
                    <div className="table-responsive shadow-sm">
                        <table className="table table-bordered table-striped align-middle">
                            <thead>
                                <tr>
                                    <th scope="col" id="App-header-table" style={{ width: '80px' }}>ID</th>
                                    <th scope="col" id="App-header-table">Relatório Base</th>
                                    <th scope="col" id="App-header-table">Título do Arquivo</th>
                                    <th scope="col" id="App-header-table" className="text-center">Colunas</th>
                                    <ProtectedAction>
                                        <th scope="col" id="App-header-table" className="text-center">Ações</th>
                                    </ProtectedAction>
                                </tr>
                            </thead>
                            <tbody>
                                {layouts.map((item) => (
                                    <tr key={item.id}>
                                        <td>{item.id}</td>
                                        <td className="fw-bold">{item.nome}</td>
                                        <td>{item.tituloArquivo}</td>
                                        <td className="text-center">
                                            <span className="badge bg-secondary">
                                                {item.layoutJson ? JSON.parse(item.layoutJson).length : 0} Campos
                                            </span>
                                        </td>

                                        <ProtectedAction>
                                            <td className="text-center">
                                                <div className="d-flex justify-content-center gap-2">
                                                    <Button
                                                        variant="link"
                                                        className="p-0"
                                                        onClick={() => navigate(`${slug ? `/${slug}` : ''}/relatorios-personalizados`, { state: { layout: item } })}
                                                    >
                                                        <ButtonAlterar />
                                                    </Button>

                                                    {item.tituloArquivo?.toUpperCase() !== 'PADRÃO' &&
                                                        item.tituloArquivo?.toUpperCase() !== 'PADRAO' ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger border-0"
                                                            onClick={() => prepararExclusao(item.id)}
                                                            title="Excluir Layout"
                                                            style={{ background: 'transparent' }}
                                                        >
                                                            <FontAwesomeIcon icon={faTrashAlt} />
                                                        </button>
                                                    ) : (
                                                        <div style={{ width: '32px' }}></div>
                                                    )}
                                                </div>
                                            </td>
                                        </ProtectedAction>
                                    </tr>
                                ))}
                                {layouts.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="text-center py-4 text-muted">Nenhum layout cadastrado.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/* Componente de Paginação adicionado abaixo da tabela */}
                        <div className="mt-4">
                            <ResponsivePagination
                                current={paginaAtual}
                                total={totalPaginas}
                                onPageChange={(page) => setPaginaAtual(page)}
                            />
                        </div>

                        {confirmOpen && (
                            <ConfirmModal
                                message="Tem certeza que deseja excluir permanentemente este layout de relatório?"
                                onConfirm={handleConfirmExclusao}
                                onCancel={() => {
                                    setConfirmOpen(false);
                                    setIdParaExcluir(null);
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
        </>
    );
}

export default ListagemLayoutRelatorioComponent;